import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi, type MfaSetupResponse } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { homePathForRole } from '../auth/home-path.js';

type Stage = 'loading' | 'ready' | 'error';

export function MfaSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const { promote, clear } = useAuth();

  const [stage, setStage] = useState<Stage>('loading');
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [codesAck, setCodesAck] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authApi
      .mfaSetupBegin()
      .then((res) => {
        if (!cancelled) {
          setSetup(res);
          setStage('ready');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStage('error');
        if (err instanceof ApiError && err.code === 'mfa_already_configured') {
          navigate('/mfa/verify', { replace: true });
        } else {
          setError('No se pudo iniciar el setup de MFA. Vuelve a iniciar sesion.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function handleConfirm(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await authApi.mfaSetupConfirm(code.trim());
      promote(res.token, res.expires_at, res.user);
      navigate(homePathForRole(res.user.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'setup_expired') {
        setError('El setup expiro. Vuelve a iniciar sesion.');
        setTimeout(() => {
          clear();
          navigate('/login', { replace: true });
        }, 1500);
      } else if (err instanceof ApiError && err.code === 'invalid_code') {
        setError('Codigo TOTP invalido.');
      } else {
        setError('No se pudo confirmar el setup.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (stage === 'loading') {
    return (
      <main className="flex min-h-full items-center justify-center px-6 py-12">
        <p className="text-sm text-slate-500">Cargando setup de MFA...</p>
      </main>
    );
  }

  if (stage === 'error' || !setup) {
    return (
      <main className="flex min-h-full items-center justify-center px-6 py-12">
        <div className="text-center space-y-3">
          <p role="alert" className="text-sm text-red-700">
            {error ?? 'Error desconocido.'}
          </p>
          <button
            type="button"
            onClick={() => {
              clear();
              navigate('/login', { replace: true });
            }}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm text-white hover:bg-brand-700/90"
          >
            Volver al login
          </button>
        </div>
      </main>
    );
  }

  const canSubmit = codesAck && /^\d{6}$/.test(code) && !submitting;

  return (
    <main className="mx-auto max-w-2xl px-6 py-10 space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-brand-900">Configura tu segundo factor</h1>
        <p className="text-sm text-slate-600">
          Escanea el codigo QR con tu app de autenticacion (Google Authenticator, 1Password,
          Authy...) y guarda los codigos de recuperacion en un lugar seguro.
        </p>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-medium text-slate-900">1. Escanea el QR</h2>
        <div className="flex justify-center">
          <img
            src={setup.qr_data_url}
            alt="Codigo QR TOTP"
            className="w-60 h-60 rounded-lg border border-slate-200"
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-medium text-slate-900">2. Guarda estos codigos de recuperacion (10)</h2>
        <p className="text-xs text-slate-500">
          Te permitiran recuperar el acceso si pierdes tu dispositivo. Cada uno se usa una sola vez.
          No los compartas y no se vuelven a mostrar.
        </p>
        <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
          {setup.recovery_codes.map((c) => (
            <li
              key={c}
              className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-slate-800 tracking-wider"
            >
              {c}
            </li>
          ))}
        </ul>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={codesAck}
            onChange={(e) => setCodesAck(e.target.checked)}
            className="mt-1 h-4 w-4"
          />
          <span>He guardado los 10 codigos en un lugar seguro fuera de este dispositivo.</span>
        </label>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-medium text-slate-900">3. Ingresa el codigo de 6 digitos</h2>
        <form
          onSubmit={(e) => {
            void handleConfirm(e);
          }}
          className="space-y-4"
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-lg border border-slate-300 px-3 py-3 text-center tracking-[0.4em] font-mono text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            placeholder="123456"
          />
          {error && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {submitting ? 'Verificando…' : 'Confirmar y entrar'}
          </button>
        </form>
      </section>
    </main>
  );
}
